/**
 * Thunk Utilities
 *
 * Common utilities for Redux async thunks.
 * Provides standardized error handling.
 */
import { OnboardingApiError } from '@contracts';
import { logger } from '@/shared/utils/logger';

/**
 * Handles errors in async thunks with standardized logging and message extraction
 *
 * This function:
 * 1. Logs the error with the provided context
 * 2. Extracts the appropriate error message based on error type
 * 3. Returns the message for use with rejectWithValue
 *
 * @param error - The caught error
 * @param context - Description of what operation failed (for logging)
 * @param defaultMessage - Default message if error type is unknown
 * @returns Error message string for rejectWithValue
 *
 * @example
 * ```typescript
 * catch (error) {
 *   return rejectWithValue(handleThunkError(error, 'fetch config', 'Failed to fetch config'));
 * }
 * ```
 */
export function handleThunkError(error: unknown, context: string, defaultMessage: string): string {
  logger.error(`Failed to ${context}:`, error);

  if (error instanceof OnboardingApiError) {
    return error.errorDetails?.errorDescription ?? error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return defaultMessage;
}
